import { Injectable } from '@angular/core';

/** Pixel delta above which a wheel event is treated as a mouse wheel. */
const MOUSE_WHEEL_PIXEL_THRESHOLD = 16;

/** Extra px before absolute scroll boundary where mouse handler takes over. */
const BOUNDARY_SLACK_PX = 24;

const WHEEL_LISTENER_OPTIONS: AddEventListenerOptions = {
  passive: false,
  capture: true
};

/**
 * Embedded loyalty runs in a fixed-height ROMA iframe. When the parent header
 * plus iframe exceed the viewport, the iframe bottom is clipped.
 *
 * Production (cross-origin ROMA): CSS-only — `overscroll-behavior-y: auto` on
 * `.views--embedded` and `overflow: hidden` on `body.embedded-view` enable
 * native scroll chaining without DOM mutation.
 *
 * Same-origin (local embed testing): forwards mouse wheel at inner scroll
 * boundaries to the parent via scrollBy.
 */
@Injectable({ providedIn: 'root' })
export class EmbeddedScrollBridgeService {
  private scroller: HTMLElement | null = null;
  private isSameOriginParent: boolean | null = null;
  private readonly onWheel = (event: WheelEvent) => this.handleWheel(event);

  attach(scroller: HTMLElement): void {
    this.detach();
    this.scroller = scroller;

    if (window.parent !== window && this.canScrollParentProgrammatically()) {
      document.addEventListener('wheel', this.onWheel, WHEEL_LISTENER_OPTIONS);
    }
  }

  detach(): void {
    document.removeEventListener('wheel', this.onWheel, WHEEL_LISTENER_OPTIONS);
    this.scroller = null;
  }

  private handleWheel(event: WheelEvent): void {
    if (!this.scroller || event.deltaY === 0 || !this.isMouseWheel(event)) {
      return;
    }

    if (!this.shouldForwardToParent(event)) {
      return;
    }

    event.preventDefault();
    this.tryFocusParent();
    this.snapScrollerToBoundary(event.deltaY);
    this.scrollParent(event.deltaY);
  }

  /** Finish any remaining inner scroll so parent scroll starts on same notch. */
  private snapScrollerToBoundary(deltaY: number): void {
    if (!this.scroller) {
      return;
    }

    if (deltaY > 0) {
      const remainingDown = this.getRemainingScrollDown(this.scroller);
      if (remainingDown > 0) {
        this.scroller.scrollTop += remainingDown;
      }
      return;
    }

    if (this.scroller.scrollTop > 0) {
      this.scroller.scrollTop = 0;
    }
  }

  private isMouseWheel(event: WheelEvent): boolean {
    if (
      event.deltaMode === WheelEvent.DOM_DELTA_LINE ||
      event.deltaMode === WheelEvent.DOM_DELTA_PAGE
    ) {
      return true;
    }
    return Math.abs(event.deltaY) >= MOUSE_WHEEL_PIXEL_THRESHOLD;
  }

  private getWheelDeltaPixels(event: WheelEvent): number {
    if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
      return Math.min(Math.abs(event.deltaY) * 40, 160);
    }
    if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
      return Math.abs(event.deltaY) * (this.scroller?.clientHeight ?? 600);
    }
    return Math.abs(event.deltaY);
  }

  /**
   * True when every scroll container under the pointer has no remaining room
   * in the wheel direction (with slack so the transition notch is handled).
   */
  private shouldForwardToParent(event: WheelEvent): boolean {
    if (!this.scroller) {
      return false;
    }

    const slack = this.getWheelDeltaPixels(event) + BOUNDARY_SLACK_PX;
    const isScrollingDown = event.deltaY > 0;
    let node = event.target instanceof Element ? event.target : null;

    while (node) {
      if (node instanceof HTMLElement && this.canElementScroll(node)) {
        if (isScrollingDown && this.canScrollDown(node, slack)) {
          return false;
        }
        if (!isScrollingDown && this.canScrollUp(node, slack)) {
          return false;
        }
      }
      node = node.parentElement;
    }

    return true;
  }

  private canElementScroll(element: HTMLElement): boolean {
    if (element.scrollHeight <= element.clientHeight + 1) {
      return false;
    }

    const overflowY = window.getComputedStyle(element).overflowY;
    return (
      overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay'
    );
  }

  private getRemainingScrollDown(element: HTMLElement): number {
    return Math.max(
      0,
      element.scrollHeight - element.clientHeight - element.scrollTop
    );
  }

  private canScrollDown(element: HTMLElement, slack: number): boolean {
    return this.getRemainingScrollDown(element) > slack;
  }

  private canScrollUp(element: HTMLElement, slack: number): boolean {
    return element.scrollTop > slack;
  }

  private canScrollParentProgrammatically(): boolean {
    if (this.isSameOriginParent !== null) {
      return this.isSameOriginParent;
    }

    if (window.parent === window) {
      this.isSameOriginParent = false;
      return false;
    }

    try {
      void window.parent.document;
      this.isSameOriginParent = true;
    } catch {
      this.isSameOriginParent = false;
    }

    return this.isSameOriginParent;
  }

  private tryFocusParent(): void {
    try {
      window.parent.focus();
    } catch {
      // Same-origin only — ignore if blocked.
    }
  }

  private scrollParent(deltaY: number): void {
    try {
      window.parent.scrollBy({ top: deltaY, left: 0, behavior: 'auto' });
    } catch {
      // Listener is not registered cross-origin.
    }
  }
}
